import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import type {
  ApiResponse,
  PaginatedResponse,
  HolidayAllowanceScheme,
  HolidayAllowanceSchemeWithCount,
  CreateSchemeDTO,
  UpdateSchemeDTO,
  ApprovalRuleCadenceInput,
  ApprovalRuleUserInput,
  NamedApprovalRuleInput,
  ApprovalRulesOverviewResponse,
  ApprovalRuleResult,
  ApprovalRuleAssignResult,
  ApprovalRuleDeleteResult,
  ApprovalRuleDeleteDefaultResult,
  NamedApprovalRuleResult,
  NamedApprovalRuleDeleteResult,
  PodAbsenceCapacity,
  PodFilterOptions,
  PodMetricsMap,
} from '../types';
import type {
  MerchantApiRegistrationResponse,
  MerchantApiRegisterResponse,
} from '../types/serviceAccount';
import { runtimeConfig } from '../config/runtime';
import { isDefinitiveAuthFailure, refreshAccessToken } from './authRefresh';

/** Axios request config plus our "already retried after a refresh" marker. */
type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * Google Chat integration config (docs/google-chat-interactive.md), as
 * returned by / accepted by `/system-config`. `serviceAccountKey` is masked to
 * '' on GET; `serviceAccountKeyConfigured` tells the UI whether a key is stored.
 * On PUT, send '' to preserve the stored key, the '__CLEAR__' sentinel to erase
 * it, or a fresh SA JSON to replace it.
 */
/**
 * BreatheHR internal sync (shadow models -> internal domain models) result
 * shape, mirroring backend `MasterSyncResult`
 * (breathhrInternalSyncOrchestrator.ts). Runs as a background job — see
 * triggerBreatheHRInternalSync / getBreatheHRInternalSyncStatus below.
 */
export interface BreatheHRSyncServiceResult {
  service: string;
  displayName: string;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  errorDetails: Array<{ id: string; error: string }>;
  duration: number;
}

export interface BreatheHRMasterSyncResult {
  success: boolean;
  totalCreated: number;
  totalUpdated: number;
  totalSkipped: number;
  totalErrors: number;
  services: BreatheHRSyncServiceResult[];
  duration: number;
}

export interface ChatModuleConfig {
  webhook: { enabled: boolean; webhookUrl: string };
  chatApp: { enabled: boolean; space: string };
}

/** Chat-app-only module config — no `webhook` variant (e.g. presence board). */
export interface ChatAppOnlyModuleConfig {
  chatApp: {
    enabled: boolean;
    space: string;
    /** Room messages after which the board card is re-posted to the bottom (1–50, default 5). */
    repostAfterMessages?: number;
    /**
     * FAZA 4 (docs/worktime-presence.md): bare, lowercase domains (no `@`)
     * from which an unmapped Google Chat clicker's verified email auto-
     * creates a Fulcrum user. Empty = disabled (default).
     */
    autoProvisionDomains?: string[];
  };
}

export interface ChatIntegrationConfig {
  chatApp: {
    enabled: boolean;
    serviceAccountKey: string;
    /** Present on GET only — the raw key is never returned. */
    serviceAccountKeyConfigured?: boolean;
    projectNumber: string;
    appName: string;
  };
  modules: {
    ssr: ChatModuleConfig;
    infra: ChatModuleConfig;
    presence: ChatAppOnlyModuleConfig;
  };
}

/** A Google Chat space the shared Chat app belongs to (item 7b selector). */
export interface ChatSpace {
  name: string;
  displayName: string;
}

/** Response of GET /system-config/chat-spaces. */
export interface ChatSpacesResult {
  success: boolean;
  configured: boolean;
  spaces: ChatSpace[];
  warning?: string;
}

/**
 * GET /system-config/health — mirrors backend/src/services/systemHealthService.ts.
 * Only measured values; `status` is derived from the Mongo / Redis checks.
 */
export type SystemHealthStatus = 'healthy' | 'warning' | 'error';

export interface SystemHealthDependency {
  status: 'up' | 'down';
  latencyMs: number | null;
  detail?: string;
}

export interface SystemHealthReport {
  status: SystemHealthStatus;
  checkedAt: string;
  process: {
    uptimeSeconds: number;
    nodeVersion: string;
    pid: number;
    /** Bytes. */
    memory: { rss: number; heapUsed: number; heapTotal: number };
  };
  mongo: SystemHealthDependency & { readyState: string };
  redis: SystemHealthDependency;
}

/**
 * Filter params shared by GET /projects (list) and GET /projects/stats (aggregates) —
 * both run through backend `buildProjectListFilter`. `'all'` means "no filter".
 */
export interface ProjectListFilterParams {
  type?: string;
  status?: string;
  priority?: string;
  complexity?: string;
  clientId?: string;
  assignedPods?: string | string[];
  projectManagerId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

/** `data` of GET /projects/stats — aggregates over the full filtered dataset. */
export interface ProjectStatsResponse {
  overview: {
    totalProjects: number;
    activeProjects: number;
    completedProjects: number;
    totalBudget: number;
    totalEstimatedHours: number;
    avgEstimatedHours: number | null;
  };
  byStatus: Array<{ _id: string; count: number; totalBudget: number; totalHours: number }>;
  byComplexity: Array<{ _id: string | null; count: number; avgHours: number | null }>;
}

/** Query accepted by GET /audit-logs and GET /audit-logs/export. */
export interface AuditLogQuery {
  page?: number;
  limit?: number;
  action?: string;
  resource?: string;
  user?: string;
}

/** One row of GET /audit-logs (lean doc, `user` populated; null when the user was deleted). */
export interface AuditLogRow {
  _id: string;
  timestamp: string;
  user: { _id: string; name?: string; email?: string } | null;
  action: string;
  resource: string;
  resourceId?: string;
  details?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  success?: boolean;
  ipAddress?: string;
  userAgent?: string;
}

export interface AuditLogListResult {
  success: boolean;
  results: AuditLogRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

/**
 * Worktime report types (docs/worktime-presence.md, item 7/8) — mirror
 * backend/src/services/worktime/worktimeReportService.ts field-for-field.
 */
export interface WorktimeUserDay {
  start?: string; // ISO of firstStartAt
  end?: string; // ISO of lastStopAt (undefined while still open today)
  breakMinutes: number;
  workedMinutes: number;
  autoClosed: boolean;
}

export interface WorktimeReportUser {
  userId: string;
  name: string;
  /** Pod names for CURRENT membership only — not historical for the report range. */
  pods: string[];
  days: Record<string, WorktimeUserDay>; // key = dayKey; only days with events
  totalWorkedMinutes: number;
  totalBreakMinutes: number;
}

export interface WorktimeReport {
  from: string;
  to: string;
  days: string[]; // every dayKey in range, for table columns
  users: WorktimeReportUser[];
}

/** Response of GET /worktime/report — `data`, not `result` (route uses res.json({ success, data })). */
export interface WorktimeReportResult {
  success: boolean;
  data: WorktimeReport;
}

/**
 * Worktime HR team-calendar matrix types (docs/worktime-hr-reporting.md, H2/H5) —
 * mirror backend/src/services/worktime/worktimeMatrixService.ts and
 * worktimeDayReport.ts field-for-field. `blocks[].from/to` and
 * `firstStart`/`lastStop` are ISO strings (serialized server-side).
 */
export interface WorktimeMatrixBlock {
  kind: 'work' | 'break';
  from: string;
  to: string;
}

export interface WorktimeMatrixDaySummary {
  workedMinutes: number;
  breakMinutes: number;
  location: 'office' | 'remote';
  /** Undefined when the day has no covering absence. */
  absenceType?: 'holiday' | 'sick' | 'other';
  autoClosed: boolean;
  adjusted: boolean;
  firstStart?: string;
  lastStop?: string;
  blocks: WorktimeMatrixBlock[];
}

export interface WorktimeMatrixTotals {
  workedMinutes: number;
  breakMinutes: number;
  officeDays: number;
  remoteDays: number;
  leaveDays: number;
  sickDays: number;
  autoClosedDays: number;
  adjustedDays: number;
}

/**
 * Office attendance rule verdict for one evaluation window
 * (docs/worktime-office-rules.md, R3). `excused` is a `violated` window that
 * somebody granted a single-window exception for (R8) — it never counts as a
 * violation, but it is NOT the same as `met`.
 */
export type WorktimeMatrixComplianceStatus = 'met' | 'violated' | 'waived' | 'pending' | 'excused';

/**
 * One office-rule window. `from`/`to` are inclusive 'YYYY-MM-DD' day keys already
 * CLAMPED to the requested matrix range (so they can be painted straight onto the
 * day columns), while `status` is computed over the FULL window server-side.
 *
 * `windowFrom`/`windowTo` are the window's FULL, unclamped bounds and are always
 * present. They are the window's IDENTITY: an exception must be granted against
 * them, never against the clamped `from`/`to` — a waiver anchored on a clamped
 * start matches no window and stays inert (docs/worktime-office-rules.md, R8
 * decision 3).
 */
export interface WorktimeMatrixComplianceWindow {
  from: string;
  to: string;
  windowFrom: string;
  windowTo: string;
  status: WorktimeMatrixComplianceStatus;
  /** Present ONLY on `status: 'excused'` (absent keys, not nulls, elsewhere). */
  waiverId?: string;
  waiverReason?: string;
  /** `null` when the granting user no longer exists — the reason never disappears. */
  grantedByName?: string | null;
}

export interface WorktimeMatrixUser {
  userId: string;
  name: string;
  department: string;
  division: string;
  pods: string[];
  /** Only days with events or absence coverage — no-record days are omitted (⚪ on the frontend). */
  days: Record<string, WorktimeMatrixDaySummary>;
  totals: WorktimeMatrixTotals;
  /**
   * Office attendance rule windows, chronologically. An EMPTY array means "no
   * effective rule" (exempt / no default / windows dropped by `effectiveFrom`) —
   * NOT "all good": such a user must never be highlighted.
   */
  compliance: WorktimeMatrixComplianceWindow[];
}

export interface WorktimeMatrixStats {
  users: number;
  avgWorkedMinutesPerActiveDay: number;
  officeDays: number;
  remoteDays: number;
  leaveDays: number;
  sickDays: number;
  autoClosedDays: number;
  adjustedDays: number;
  usersWithNoRecords: number;
  /**
   * Number of `violated` office-rule windows across the FULL filtered dataset
   * (pagination-stats rule); one person with two missed weeks counts twice.
   * `pending`/`waived`/`excused` windows are never counted, so granting an
   * exception lowers this number.
   */
  violationsCount: number;
}

export interface WorktimeMatrix {
  days: string[];
  users: WorktimeMatrixUser[];
  stats: WorktimeMatrixStats;
}

/** Response of GET /worktime/matrix — `data`, not `result` (route uses res.json({ success, data })). */
export interface WorktimeMatrixResult {
  success: boolean;
  data: WorktimeMatrix;
}

export interface WorktimeMatrixParams {
  from: string;
  to: string;
  podIds?: string[];
  departments?: string[];
  divisions?: string[];
  search?: string;
}

/**
 * Office attendance rule management types (docs/worktime-office-rules.md, R2/R5/R6)
 * — mirror the `OfficeRuleResponse` / `NamedOfficeRuleResponse` /
 * `EffectiveOfficeRuleResponse` serializers in backend/src/routes/worktime.ts
 * field-for-field. Every endpoint is gated by `worktime:rules` (a SEPARATE
 * action from `worktime:manage`), so the UI must not even fetch without it.
 */
export interface OfficeRuleInput {
  periodWeeks: 1 | 2;
  requiredOfficeDays: number;
  /** ISO weekdays, 1=Mon..7=Sun. Empty/omitted = every day counts. */
  countedWeekdays?: number[];
  /** ISO date/date-time. Omitted = the server keeps/derives it (creation date). */
  effectiveFrom?: string;
  active?: boolean;
}

/** Body of POST/PUT /worktime/office-rules/rules — a reusable named rule (R6). */
export interface NamedOfficeRuleInput extends OfficeRuleInput {
  /** Unique among named rules, case-insensitively (409 on a duplicate). */
  name: string;
}

/**
 * A per-user override is EXACTLY ONE of three forms (R6): exempt, linked to a
 * named rule, or its own inline cadence. Mixing them is a deliberate 400 rather
 * than a silent precedence, hence the union — a rule-linked body must not carry
 * cadence fields at all, and `effectiveFrom` then means "from when THIS person
 * follows the rule" (the later of the two dates wins server-side).
 */
export type OfficeRuleUserInput =
  | (OfficeRuleInput & { exempt?: false; ruleId?: undefined })
  | { exempt: true; effectiveFrom?: string; active?: boolean }
  | { ruleId: string; effectiveFrom?: string; active?: boolean };

/** A stored rule document (default, per-user override or named rule) as the API exposes it. */
export interface OfficeRule {
  id: string;
  scope: 'default' | 'user' | 'named';
  userId: string | null;
  /** Non-null only for `scope: 'named'`. */
  name: string | null;
  /** Non-null only on a rule-linked override — the named rule it follows. */
  ruleId: string | null;
  /** The linked rule's label, when the handler knows it (PUT /office-rules/:userId). */
  ruleName: string | null;
  exempt: boolean;
  /**
   * `null` on a rule-linked override ONLY: such a document deliberately stores
   * no cadence (the named rule owns it), so the serializer refuses to invent
   * one. Default rules, inline overrides and named rules always carry numbers.
   */
  periodWeeks: number | null;
  requiredOfficeDays: number | null;
  countedWeekdays: number[] | null;
  effectiveFrom: string;
  active: boolean;
  updatedAt: string | null;
}

/** A named rule plus how many people currently follow it (inactive overrides included). */
export interface NamedOfficeRule extends OfficeRule {
  assignedCount: number;
}

/**
 * The cadence actually applying to one user — `source` is a SIBLING field, not
 * nested. For `source: 'rule'` these numbers come from the NAMED rule, and
 * `effectiveFrom` is the LATER of the assignment's and the rule's dates.
 */
export interface EffectiveOfficeRule {
  periodWeeks: number;
  requiredOfficeDays: number;
  countedWeekdays: number[];
  effectiveFrom: string;
}

/**
 * Where a user's effective rule comes from. `exempt` and `none` both mean "not
 * checked", but only `exempt` is a deliberate per-user decision — `none` also
 * covers "no default configured" and "default is inactive". `rule` is an
 * override pointing at a named rule; `override` is an inline personal cadence.
 */
export type OfficeRuleSource = 'default' | 'override' | 'rule' | 'exempt' | 'none';

export interface OfficeRuleAssignment {
  userId: string;
  name: string;
  /** `''` when the user has no department set (never undefined — mirrors the matrix). */
  department: string;
  effective: EffectiveOfficeRule | null;
  source: OfficeRuleSource;
  /** Non-null EXCLUSIVELY for `source: 'rule'` (the keys are present on every row). */
  ruleId: string | null;
  ruleName: string | null;
}

/** Response of GET /worktime/office-rules — `data`, not `result`. */
export interface OfficeRulesResult {
  success: boolean;
  data: {
    /** An INACTIVE default is still returned here (with `active: false`) while every user reads `source: 'none'`. */
    default: OfficeRule | null;
    /** Every named rule in the database, sorted by name — no other ordering guarantee. */
    rules: NamedOfficeRule[];
    users: OfficeRuleAssignment[];
  };
}

/** Response of PUT /worktime/office-rules/default and PUT /worktime/office-rules/:userId. */
export interface OfficeRuleResult {
  success: boolean;
  data: OfficeRule;
}

/** Response of POST (201) and PUT /worktime/office-rules/rules[/:ruleId]. */
export interface NamedOfficeRuleResult {
  success: boolean;
  data: NamedOfficeRule;
}

/**
 * Response of POST /worktime/office-rules/rules/:ruleId/assign. `replaced` says
 * what each person had before, so the UI can report "2 assigned, 1 was exempt".
 */
export interface OfficeRuleAssignResult {
  success: boolean;
  data: {
    ruleId: string;
    ruleName: string;
    assignedCount: number;
    users: Array<{
      userId: string;
      name: string;
      replaced: 'exempt' | 'override' | 'rule' | 'none';
    }>;
  };
}

/** Response of DELETE /worktime/office-rules/:userId. */
export interface OfficeRuleDeleteResult {
  success: boolean;
  data: { userId: string; removed: boolean };
}

/** Response of DELETE /worktime/office-rules/rules/:ruleId. */
export interface NamedOfficeRuleDeleteResult {
  success: boolean;
  data: { ruleId: string; removed: boolean };
}

/** Response of DELETE /worktime/office-rules/default. */
export interface OfficeRuleDeleteDefaultResult {
  success: boolean;
  data: { scope: 'default'; removed: boolean };
}

/**
 * Single-window exception ("waiver", docs/worktime-office-rules.md, R8/R9) —
 * mirrors the `OfficeRuleWaiverResponse` serializer shared by POST/PUT/GET.
 * The window bounds ARE the waiver's identity, which is why only the reason is
 * ever editable; moving an exception is a delete plus a fresh grant.
 */
export interface OfficeRuleWaiver {
  id: string;
  userId: string;
  windowFrom: string;
  windowTo: string;
  reason: string;
  grantedBy: string | null;
  grantedByName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Body of POST /worktime/office-rules/waivers. */
export interface OfficeRuleWaiverInput {
  userId: string;
  /** FULL window bounds from `compliance[].windowFrom`, never the clamped `from`. */
  windowFrom: string;
  windowTo: string;
  reason: string;
}

/** Response of POST (201) and PUT /worktime/office-rules/waivers[/:id]. */
export interface OfficeRuleWaiverResult {
  success: boolean;
  data: OfficeRuleWaiver;
}

/** Response of DELETE /worktime/office-rules/waivers/:id. */
export interface OfficeRuleWaiverDeleteResult {
  success: boolean;
  data: { waiverId: string; removed: boolean };
}

/**
 * Worktime HR single-person report types (docs/worktime-hr-reporting.md, H3/H6)
 * — mirror backend/src/services/worktime/worktimePersonReportService.ts
 * field-for-field. Every day summary field matches `WorktimeMatrixDaySummary`
 * exactly (both serialize the same `WorktimeReportDaySummary`), so the report
 * day just extends it with the day key + weekend flag.
 */
export interface WorktimePersonReportUser {
  userId: string;
  name: string;
  department: string;
  division: string;
  pods: string[];
}

export interface WorktimePersonReportDay extends WorktimeMatrixDaySummary {
  day: string;
  weekend: boolean;
}

export interface WorktimePersonReport {
  user: WorktimePersonReportUser;
  from: string;
  to: string;
  /** Every calendar day in [from, to] — including days with no events/absence. */
  days: WorktimePersonReportDay[];
  totals: WorktimeMatrixTotals;
}

/** Response of GET /worktime/users/:id/report — `data`, not `result`. */
export interface WorktimePersonReportResult {
  success: boolean;
  data: WorktimePersonReport;
}

/**
 * Worktime HR day-correction types (docs/worktime-hr-reporting.md, H4/H6) —
 * mirror backend/src/services/worktime/worktimeCorrectionService.ts. Blocks
 * submitted in the PUT body are always closed (`to` required), unlike the
 * live derived `WorktimeMatrixBlock`.
 */
export interface WorktimeDayCorrectionBlockInput {
  kind: 'work' | 'break';
  from: string; // ISO
  to: string; // ISO
}

export interface WorktimeDayCorrectionRequest {
  blocks: WorktimeDayCorrectionBlockInput[];
  location: 'office' | 'remote';
  note: string;
}

export interface WorktimeDayCorrectionResponse {
  batchId: string;
  supersededCount: number;
  insertedCount: number;
  day: WorktimeMatrixDaySummary & { day: string };
}

/** Response of PUT /worktime/users/:id/day/:day — `data`, not `result`. */
export interface WorktimeDayCorrectionResult {
  success: boolean;
  data: WorktimeDayCorrectionResponse;
}

/**
 * Presence Board Settings types (docs/worktime-presence.md, FAZA 2 / item S2) —
 * mirror backend/src/routes/worktime.ts's `PresenceSettingsResponse` /
 * `loadPresenceSettingsContext` field-for-field.
 */
export interface PresenceDepartmentOrder {
  podId: string;
  departments: string[];
}

export interface PresenceBoardSettings {
  excludedUserIds: string[];
  podOrder: string[];
  departmentOrders: PresenceDepartmentOrder[];
}

/** Response of GET/PUT /worktime/presence-settings — `data`, not `result`. */
export interface PresenceSettingsResult {
  success: boolean;
  data: PresenceBoardSettings;
}

/** Response of PUT /auth/timezone (docs/worktime-presence.md, FAZA 3, T2) — `data`, not `result`. */
export interface MyTimezoneResult {
  success: boolean;
  data: { timezone: string };
}

export interface PresenceContextPod {
  id: string;
  name: string;
  departments: string[];
}

export interface PresenceContextUser {
  id: string;
  name: string;
  department: string;
}

export interface PresenceSettingsContext {
  pods: PresenceContextPod[];
  users: PresenceContextUser[];
  /** Distinct sorted departments of active users — Chat Surveys audience picker. */
  departments: string[];
}

/** Response of GET /worktime/presence-settings/context — `data`, not `result`. */
export interface PresenceSettingsContextResult {
  success: boolean;
  data: PresenceSettingsContext;
}

/** Subset of the system-config document the admin tabs read/round-trip. */
export interface SystemConfigResult {
  general?: unknown;
  security?: unknown;
  authentication?: unknown;
  performance?: unknown;
  chatIntegration?: ChatIntegrationConfig;
  [key: string]: unknown;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: runtimeConfig.apiUrl + '/api',
      timeout: 30000, // Increased to 30 seconds for complex analytics queries
      withCredentials: true, // Enable cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor: refresh once on 401, then retry the original request.
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as RetryableRequestConfig | undefined;
        const requestUrl = originalRequest?.url ?? '';

        // Auth endpoints own their failures: refreshing a refresh would loop and
        // a failed login must reach the login form untouched.
        const isAuthEndpoint =
          requestUrl.includes('/auth/refresh') || requestUrl.includes('/auth/login');

        if (
          error.response?.status !== 401 ||
          !originalRequest ||
          isAuthEndpoint ||
          originalRequest._retry
        ) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        let newToken: string;
        try {
          newToken = await refreshAccessToken();
        } catch (refreshError) {
          // Only a server verdict on the token (401/403, or no token at all)
          // ends the session. Transient failures - network error, timeout,
          // 5xx - must leave the session alone; they used to log everyone out.
          if (!isDefinitiveAuthFailure(refreshError)) {
            return Promise.reject(error);
          }
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        // A 401 on the retry hits the `_retry` guard above and is rejected - no loop.
        return this.api(originalRequest);
      }
    );
  }

  // Generic methods
  async get<T>(url: string, params?: any, config?: any): Promise<ApiResponse<T>> {
    // `config` merges extra axios options (e.g. a per-request timeout
    // for slow report endpoints) without breaking the (url, params)
    // call shape used everywhere else.
    const response: AxiosResponse<ApiResponse<T>> = await this.api.get(url, { params, ...config });
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.api.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.api.put(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.api.patch(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.api.delete(url);
    return response.data;
  }

  async getPaginated<T>(url: string, params?: any): Promise<PaginatedResponse<T>> {
    const response: AxiosResponse<PaginatedResponse<T>> = await this.api.get(url, { params });
    return response.data;
  }

  // Authentication methods
  async login(email: string, password: string) {
    return this.post<{ token: string; user: any }>('/auth/login', { email, password });
  }

  async logout() {
    return this.post('/auth/logout');
  }

  /**
   * @deprecated Use `refreshAccessToken()` from `./authRefresh` instead - it is
   * single-flight and classifies failures (definitive vs transient). This raw
   * call does neither.
   */
  async refreshToken(token?: string) {
    const currentToken = token || localStorage.getItem('auth_token');
    return this.post<{ token: string }>('/auth/refresh', { token: currentToken });
  }

  async getCurrentUser() {
    return this.get<any>('/auth/me');
  }

  async getUserPermissions() {
    return this.get<{ permissions: any[] }>('/auth/permissions');
  }

  // User management methods
  async getUsers(params?: any) {
    return this.getPaginated('/users', params);
  }

  async getUser(id: string) {
    return this.get(`/users/${id}`);
  }

  async createUser(data: any) {
    return this.post('/users', data);
  }

  async updateUser(id: string, data: any) {
    return this.put(`/users/${id}`, data);
  }

  async deleteUser(id: string) {
    return this.delete(`/users/${id}`);
  }

  async deactivateUser(id: string) {
    return this.put(`/users/${id}/deactivate`);
  }

  async reactivateUser(id: string) {
    return this.put(`/users/${id}/reactivate`);
  }

  async changeUserPassword(id: string, newPassword: string) {
    return this.put(`/users/${id}/change-password`, { newPassword });
  }

  async bulkUpdateUserRoles(data: { userIds: string[]; role: string }) {
    return this.post('/users/bulk-update-roles', data);
  }

  async getUserRoleStats() {
    return this.get('/users/stats');
  }

  // Enhanced permission management methods
  async getUserPermissionSummary(userId: string) {
    return this.get(`/users/${userId}/permissions`);
  }

  async grantPermissionOverride(userId: string, override: any) {
    return this.post(`/users/${userId}/permission-overrides`, override);
  }

  async revokePermissionOverride(userId: string, data: { resource: string; action: string; scope: string }) {
    return this.delete(`/users/${userId}/permission-overrides`, { data });
  }

  async bulkGrantPermissionOverrides(data: { userIds: string[]; override: any }) {
    return this.post('/users/bulk/permission-overrides', data);
  }

  async bulkRevokePermissionOverrides(data: { userIds: string[]; resource: string; action: string; scope: string }) {
    return this.delete('/users/bulk/permission-overrides', { data });
  }

  async getRolePermissions(role: string) {
    return this.get(`/users/roles/${role}/permissions`);
  }

  async getAvailablePermissions() {
    return this.get('/auth/available-permissions');
  }

  async cleanupExpiredPermissions() {
    return this.post('/users/cleanup-expired-permissions');
  }

  async getPermissionCacheMetrics() {
    return this.get('/users/cache/metrics');
  }

  async warmupPermissionCache(data: { userIds?: string[]; roles?: string[] }) {
    return this.post('/users/cache/warmup', data);
  }

  // RBAC v2: Multi-role management methods
  async getAdminRoles(params?: { type?: string; isActive?: string }) {
    return this.get<any[]>('/admin/roles', params);
  }

  async setUserRoles(userId: string, roleIds: string[]) {
    return this.put(`/admin/roles/users/${userId}/roles`, { roleIds });
  }

  async addUserRoles(userId: string, roleIds: string[]) {
    return this.post(`/admin/roles/users/${userId}/roles`, { roleIds });
  }

  async removeUserRole(userId: string, roleId: string) {
    return this.delete(`/admin/roles/users/${userId}/roles/${roleId}`);
  }

  // Employee management methods
  async getEmployees(params?: any) {
    return this.getPaginated('/employees', params);
  }

  async getEmployee(id: string) {
    return this.get(`/employees/${id}`);
  }

  async createEmployee(data: any) {
    return this.post('/employees', data);
  }

  async updateEmployee(id: string, data: any) {
    return this.put(`/employees/${id}`, data);
  }

  async deleteEmployee(id: string) {
    return this.delete(`/employees/${id}`);
  }

  // Pod methods
  /**
   * `status` = active (default) | inactive | all. `skills` is comma-separated.
   * `utilizationRange` / `sortBy=utilization` are evaluated server-side over the
   * whole filtered set, so `pagination.total` is always the real total.
   */
  async getPods(params?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    status?: string;
    utilizationRange?: string;
    leadId?: string;
    skills?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    return this.getPaginated('/pods', params);
  }

  /** Options for the Pods page "Pod Lead" and "Skills" filters. */
  async getPodFilterOptions() {
    return this.get<PodFilterOptions>('/pods/filter-options');
  }

  async getPod(id: string) {
    return this.get(`/pods/${id}`);
  }

  /**
   * Get pod names for filtering/display purposes
   * This is a lightweight endpoint accessible to all authenticated users.
   *
   * The route answers `{ success, results }` — NOT the generic `ApiResponse`
   * `{ success, result }` shape — which is why every caller used to reach for
   * `(res as any).results`. Typed honestly here so callers can read `results`
   * directly (a `res.data` reader silently got `undefined`, e.g. the worktime
   * pod filter, whose option list was always empty).
   */
  async getPodNames(): Promise<{ success: boolean; results: Array<{ id: string; name: string }> }> {
    const response = await this.api.get<{
      success: boolean;
      results: Array<{ id: string; name: string }>;
    }>('/pods/names');
    return response.data;
  }

  async createPod(data: any) {
    return this.post('/pods', data);
  }

  async updatePod(id: string, data: any) {
    return this.put(`/pods/${id}`, data);
  }

  async deletePod(id: string) {
    return this.delete(`/pods/${id}`);
  }

  /** Answers `{ success, utilization }` (not the generic `result`). */
  async getPodUtilizationData(
    podId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<{ success: boolean; utilization: PodAbsenceCapacity }> {
    const response = await this.api.get<{ success: boolean; utilization: PodAbsenceCapacity }>(
      `/pods/${podId}/utilization`,
      { params }
    );
    return response.data;
  }

  /** Answers `{ success, metrics }` keyed by pod id (not the generic `result`). */
  async getPodMetrics(podIds: string[]): Promise<{ success: boolean; metrics: PodMetricsMap }> {
    const response = await this.api.get<{ success: boolean; metrics: PodMetricsMap }>('/pods/metrics', {
      params: { podIds: podIds.join(',') },
    });
    return response.data;
  }

  // Pod Assignment methods
  async getPodAssignments(params?: {
    page?: number;
    limit?: number;
    search?: string;
    podId?: string;
    userId?: string;
    assignmentRole?: string;
    isActive?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    return this.getPaginated('/pod-assignments', params);
  }

  async getPodAssignment(id: string) {
    return this.get(`/pod-assignments/${id}`);
  }

  async createPodAssignment(data: {
    userId: string;
    podId: string;
    assignmentRole?: string;
    capacity: number;
    startDate?: string;
  }) {
    return this.post('/pod-assignments', data);
  }

  async updatePodAssignment(id: string, data: any) {
    return this.put(`/pod-assignments/${id}`, data);
  }

  async deletePodAssignment(id: string) {
    return this.delete(`/pod-assignments/${id}`);
  }

  async bulkAssignUsersToPod(data: {
    userIds: string[];
    podId: string;
    assignmentRole?: string;
    capacity?: number
  }) {
    return this.post('/pod-assignments/bulk-assign', data);
  }

  async bulkRemoveUsersFromPod(data: { userIds: string[]; podId: string }) {
    return this.post('/pod-assignments/bulk-remove', data);
  }



  // Pod direct assignment (consolidated endpoint)
  async assignUserToPod(podId: string, data: {
    userId: string;
    assignmentRole?: string;
    capacity?: number;
  }) {
    return this.post(`/pods/${podId}/assignments`, data);
  }


  async getPodMembers(podId: string, isActive: boolean = true) {
    // Use pod assignments instead of team members
    return this.getPodAssignments({
      podId,
      isActive,
      limit: 1000
    });
  }

  async getAvailableMembers(params?: {
    excludePodId?: string;
    skills?: string[];
    role?: string;
    capacity?: number;
  }) {
    // Get all active members - we'll filter on frontend
    const queryParams: any = {
      isActive: true,
      limit: 1000 // Get a large number to avoid pagination issues
    };

    if (params?.role) {
      queryParams.role = params.role;
    }

    if (params?.skills && params.skills.length > 0) {
      queryParams.skills = params.skills.join(',');
    }

    return this.getPaginated('/pod-assignments', queryParams);
  }


  async transferUserBetweenPods(userId: string, data: { fromPodId: string; toPodId: string; assignmentRole?: string; capacity?: number }) {
    // First, deactivate current assignment
    const currentAssignments = await this.get(`/pod-assignments?userId=${userId}&podId=${data.fromPodId}&isActive=true`);
    if (currentAssignments.data?.length > 0) {
      await this.put(`/pod-assignments/${currentAssignments.data[0]._id}`, {
        isActive: false,
        endDate: new Date().toISOString()
      });
    }

    // Then create new assignment
    return this.post(`/pod-assignments`, {
      userId,
      podId: data.toPodId,
      assignmentRole: data.assignmentRole || 'team_member',
      capacity: data.capacity || 40,
      startDate: new Date().toISOString()
    });
  }



  // Client methods
  async getClients(params?: any) {
    return this.getPaginated('/clients', params);
  }

  async getClient(id: string) {
    return this.get(`/clients/${id}`);
  }

  async createClient(data: any) {
    return this.post('/clients', data);
  }

  async updateClient(id: string, data: any) {
    return this.put(`/clients/${id}`, data);
  }

  async deleteClient(id: string) {
    return this.delete(`/clients/${id}`);
  }

  async getClientStats() {
    return this.get('/clients/stats');
  }

  async getClientProjects(id: string) {
    return this.get(`/clients/${id}/projects`);
  }

  async getClientRetainers(id: string) {
    return this.get(`/clients/${id}/retainers`);
  }

  // Project methods
  async getProjects(params?: any) {
    return this.getPaginated('/projects', params);
  }

  async getProject(id: string) {
    return this.get(`/projects/${id}`);
  }

  async createProject(data: any) {
    // Use the appropriate endpoint based on project type
    if (data.type === 'internal') {
      return this.post('/internal', data);
    } else {
      return this.post('/projects', data);
    }
  }

  async updateProject(id: string, data: any) {
    return this.put(`/projects/${id}`, data);
  }

  async convertProject(id: string, data: any) {
    return this.put(`/projects/${id}/convert`, data);
  }

  async deleteProject(id: string) {
    return this.delete(`/projects/${id}`);
  }

  async hardDeleteProject(id: string) {
    return this.delete(`/projects/${id}/hard`);
  }

  /** Full-dataset aggregates; accepts the same filter params as `getProjects`. */
  async getProjectStats(params?: ProjectListFilterParams): Promise<{ success: boolean; data: ProjectStatsResponse }> {
    const response = await this.api.get<{ success: boolean; data: ProjectStatsResponse }>('/projects/stats', { params });
    return response.data;
  }

  async getProjectsForReporting() {
    return this.get('/projects/reporting');
  }

  // Retainer methods
  async getRetainers(params?: any) {
    return this.getPaginated('/retainers', params);
  }

  async getRetainer(id: string) {
    return this.get(`/retainers/${id}`);
  }

  async createRetainer(data: any) {
    return this.post('/retainers', data);
  }

  async updateRetainer(id: string, data: any) {
    return this.put(`/retainers/${id}`, data);
  }

  async deleteRetainer(id: string) {
    return this.delete(`/retainers/${id}`);
  }

  async getRetainerStats() {
    return this.get('/retainers/stats');
  }

  async getRetainerWorkloadAnalysis(id: string, months?: number) {
    return this.get(`/retainers/${id}/workload-analysis`, { months });
  }

  async updateRetainerUsage(id: string, data: any) {
    return this.put(`/retainers/${id}/usage`, data);
  }

  // Internal project methods
  async getInternalProjects(params?: any) {
    return this.getPaginated('/internal', params);
  }

  async getInternalProject(id: string) {
    return this.get(`/internal/${id}`);
  }

  async createInternalProject(data: any) {
    return this.post('/internal', data);
  }

  async updateInternalProject(id: string, data: any) {
    return this.put(`/internal/${id}`, data);
  }

  async deleteInternalProject(id: string) {
    return this.delete(`/internal/${id}`);
  }

  async getInternalProjectStats() {
    return this.get('/internal/stats');
  }

  // Forecast methods
  async getForecasts(params?: any) {
    return this.get('/forecasts', params);
  }

  async generateForecast(podId: string, params?: any) {
    return this.post(`/forecasts/generate/${podId}`, params);
  }

  // Forecasting service methods
  async generatePodUtilizationForecasts(options?: {
    forecastHorizon?: number;
    confidenceLevel?: number;
    includeSeasonality?: boolean;
    modelType?: string;
    minHistoryDays?: number;
    aggregationPeriod?: string;
  }) {
    return this.post('/forecasting/pod-utilization', options);
  }

  async generatePodForecast(podId: string, options?: {
    forecastHorizon?: number;
    confidenceLevel?: number;
    includeSeasonality?: boolean;
    modelType?: string;
    minHistoryDays?: number;
    aggregationPeriod?: string;
  }) {
    return this.post(`/forecasting/pod/${podId}`, options);
  }

  async getForecastingModels() {
    return this.get('/forecasting/models');
  }

  async getForecastingHealth() {
    return this.get('/forecasting/health');
  }

  async validateForecastingData(podId: string, minHistoryDays?: number) {
    return this.post('/forecasting/validate-data', { podId, minHistoryDays });
  }

  async generateStrategicForecast(options?: {
    forecastHorizon?: number;
  }) {
    return this.post('/forecasting/strategic', options);
  }

  // Analytics methods
  async getAnalyticsMetrics(filters?: any) {
    return this.post('/analytics/metrics', filters);
  }

  async getProjectTypeAnalytics(filters?: any) {
    return this.post('/analytics/project-types', filters);
  }

  async getTimeAllocation(groupBy: string, filters?: any) {
    return this.post(`/analytics/allocation/${groupBy}`, filters);
  }

  async getTrendData(granularity: string, filters?: any) {
    return this.post(`/analytics/trends/${granularity}`, filters);
  }

  async getDistributionData(type: string, filters?: any) {
    return this.post(`/analytics/distribution/${type}`, filters);
  }

  async getTeamUtilization(filters?: any) {
    return this.post('/analytics/utilization', filters);
  }

  async exportAnalyticsData(filters?: any) {
    return this.post('/analytics/export', filters);
  }

  // Legacy methods for backward compatibility
  async getPodUtilization(podId?: string, timeRange?: string) {
    const filters: any = {};
    if (podId) filters.podIds = [podId];
    if (timeRange) {
      const endDate = new Date();
      const startDate = new Date();
      if (timeRange === '7d') startDate.setDate(endDate.getDate() - 7);
      else if (timeRange === '30d') startDate.setDate(endDate.getDate() - 30);
      else if (timeRange === '90d') startDate.setDate(endDate.getDate() - 90);

      filters.startDate = startDate.toISOString();
      filters.endDate = endDate.toISOString();
    }
    return this.getTeamUtilization(filters);
  }

  async getProjectAnalytics(timeRange?: string) {
    const filters: any = {};
    if (timeRange) {
      const endDate = new Date();
      const startDate = new Date();
      if (timeRange === '7d') startDate.setDate(endDate.getDate() - 7);
      else if (timeRange === '30d') startDate.setDate(endDate.getDate() - 30);
      else if (timeRange === '90d') startDate.setDate(endDate.getDate() - 90);

      filters.startDate = startDate.toISOString();
      filters.endDate = endDate.toISOString();
    }
    return this.getAnalyticsMetrics(filters);
  }

  async getCapacityAnalytics(timeRange?: string) {
    const filters: any = {};
    if (timeRange) {
      const endDate = new Date();
      const startDate = new Date();
      if (timeRange === '7d') startDate.setDate(endDate.getDate() - 7);
      else if (timeRange === '30d') startDate.setDate(endDate.getDate() - 30);
      else if (timeRange === '90d') startDate.setDate(endDate.getDate() - 90);

      filters.startDate = startDate.toISOString();
      filters.endDate = endDate.toISOString();
    }
    return this.getAnalyticsMetrics(filters);
  }

  // Time tracking methods
  async getTimeEntries(params?: any) {
    return this.getPaginated('/time-entries', params);
  }

  async getTimeEntriesGroupedByIssue(params?: any) {
    return this.get('/time-entries/grouped-by-issue', params);
  }

  async getTimeEntry(id: string) {
    return this.get(`/time-entries/${id}`);
  }

  async createTimeEntry(data: any) {
    return this.post('/time-entries', data);
  }

  async updateTimeEntry(id: string, data: any) {
    return this.put(`/time-entries/${id}`, data);
  }

  async deleteTimeEntry(id: string) {
    return this.delete(`/time-entries/${id}`);
  }

  // Bulk time entry operations
  async bulkUpdateTimeEntries(data: {
    entryIds: string[];
    operation: 'approve' | 'reject' | 'toggle-billable' | 'reassign' | 'update-fields';
    operationData?: any;
  }) {
    return this.post('/time-entries/bulk-update', data);
  }

  async bulkDeleteTimeEntries(entryIds: string[]) {
    return this.post('/time-entries/bulk-delete', { entryIds });
  }

  async bulkExportTimeEntries(entryIds: string[], format: 'csv' | 'pdf' = 'csv') {
    return this.post('/time-entries/bulk-export', { entryIds, format }, {
      responseType: 'blob'
    });
  }

  // Assignment-specific time entry methods
  async getTimeEntriesByAssignment(assignmentId: string, params?: any) {
    return this.getPaginated(`/time-entries/assignment/${assignmentId}`, params);
  }

  async getAssignmentTimeSummary(assignmentId: string) {
    return this.get(`/time-entries/assignment/${assignmentId}/summary`);
  }

  // Alert methods
  async getAlerts(params?: any) {
    return this.getPaginated('/alerts', params);
  }

  async markAlertAsRead(id: string) {
    return this.put(`/alerts/${id}/read`);
  }

  async deleteAlert(id: string) {
    return this.delete(`/alerts/${id}`);
  }

  // Epic methods
  async getEpics(params?: any) {
    return this.getPaginated('/epics', params);
  }

  async getEpicById(id: string) {
    return this.get(`/epics/${id}`);
  }

  async createEpic(data: any) {
    return this.post('/epics', data);
  }

  async updateEpic(id: string, data: any) {
    return this.put(`/epics/${id}`, data);
  }

  async updateEpicOutcome(id: string, data: any) {
    return this.put(`/epics/${id}/outcome`, data);
  }

  async deleteEpic(id: string) {
    return this.delete(`/epics/${id}`);
  }

  async getEpicStats() {
    return this.get('/epics/stats');
  }

  async getEpicEstimationAnalysis(params?: any) {
    return this.get('/epics/analysis/estimation', params);
  }

  async bulkUpdateEpicAssignments(epicIds: string[], action = 'update') {
    return this.post('/epics/bulk-update-assignments', { epicIds, action });
  }

  // Integration methods
  async getIntegrationHealth() {
    return this.get('/integrations/health');
  }

  async configureIntegration(config: any) {
    return this.post('/integrations/configure', { config });
  }

  async testIntegrationConnection(integrationType: string, integrationName: string, config?: any) {
    return this.post('/integrations/test', { integrationType, integrationName, config });
  }

  async getIntegrationConfigs() {
    return this.get('/integrations/configs');
  }

  async getSupportedIntegrations() {
    return this.get('/integrations/supported');
  }

  async getJiraProjects(): Promise<ApiResponse<{ projects: Array<{ key: string; name: string; id: string; projectTypeKey?: string; simplified?: boolean; avatarUrls?: any }>, cached: boolean, cacheAge: number }>> {
    return this.get('/integrations/jira/projects');
  }

  // ClickUp Integration methods
  async getClickUpWorkspaces() {
    return this.get('/integrations/clickup/workspaces');
  }

  async getClickUpSpaces(workspaceId: string) {
    return this.get(`/integrations/clickup/workspaces/${workspaceId}/spaces`);
  }

  async getClickUpBillingItems(params?: {
    workspaceId?: string;
    spaceId?: string;
    month?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    return this.get('/integrations/clickup/billing/items', params);
  }

  // System Configuration API
  async getSystemConfig() {
    return this.get('/system-config');
  }

  async updateSystemConfig(config: any) {
    return this.put('/system-config', config);
  }

  async getSystemConfigResult() {
    return this.get<SystemConfigResult>('/system-config');
  }

  /**
   * List the Google Chat spaces the shared Chat app is a member of (item 7b).
   * Returns the full `{ configured, spaces, warning }` envelope; the `get`
   * helper returns the raw response body so these top-level fields are read
   * directly (not under `result`).
   */
  async getChatSpaces() {
    return this.get<never>('/system-config/chat-spaces') as unknown as Promise<ChatSpacesResult>;
  }

  /** Real process / MongoDB / Redis health (`system:view`) — docs/system-admin-permissions.md. */
  async getSystemHealth() {
    return this.get<SystemHealthReport>('/system-config/health') as unknown as Promise<{ success: boolean; result: SystemHealthReport }>;
  }

  /**
   * Manually repost the presence board card to the bottom of the room
   * (docs/worktime-presence.md) — the same action the every-N-messages
   * auto-repost performs, triggered on demand from System Admin.
   */
  async repostPresenceBoard(): Promise<{
    success: boolean;
    result: { reposted: boolean; reason?: string };
  }> {
    return this.post<{ reposted: boolean; reason?: string }>(
      '/system-config/presence-board/repost'
    ) as unknown as Promise<{ success: boolean; result: { reposted: boolean; reason?: string } }>;
  }

  // Phase 71: Sentry org-level integration (system settings)
  async testSentryConnection() {
    return this.post('/system-config/sentry/test');
  }

  async listSentryProjects() {
    return this.get('/system-config/sentry/projects');
  }

  // Phase 77: Kubecost integration (system settings)
  async testKubecostConnection() {
    return this.post('/system-config/kubecost/test');
  }

  // Phase 90: Jenkins org-level integration (system settings)
  async listJenkinsInstances() {
    return this.get('/system-config/jenkins');
  }

  async testJenkinsConnection(params: {
    instanceId?: string;
    candidate?: { baseUrl: string; authHeader?: string };
  }) {
    return this.post('/system-config/jenkins/test', params);
  }

  async listJenkinsJobs(instanceId: string) {
    return this.get(`/system-config/jenkins/${encodeURIComponent(instanceId)}/jobs`);
  }

  // Bitbucket Cloud workspace connections (system level).
  async listBitbucketServers() {
    return this.get('/system-config/bitbucket');
  }

  // Anthropic key pool
  async listAnthropicKeys() {
    return this.get<{ success: boolean; keys: any[] }>('/system-config/anthropic-keys');
  }

  async addAnthropicKey(payload: { name: string; authType: 'oauth' | 'api_key'; key: string }) {
    return this.post('/system-config/anthropic-keys', payload);
  }

  async updateAnthropicKey(id: string, patch: { name?: string; enabled?: boolean }) {
    return this.patch(`/system-config/anthropic-keys/${encodeURIComponent(id)}`, patch);
  }

  async deleteAnthropicKey(id: string) {
    return this.delete(`/system-config/anthropic-keys/${encodeURIComponent(id)}`);
  }

  async testBitbucketConnection(params: {
    serverId?: string;
    candidate?: { workspace: string; token?: string };
  }) {
    return this.post('/system-config/bitbucket/test', params);
  }

  async listBitbucketRepositories(serverId: string) {
    return this.get(`/system-config/bitbucket/${encodeURIComponent(serverId)}/repositories`);
  }

  // Org-wide Google service-account bank (powers the GMC validator's
  // per-app picker today; reused by other features as they need it).
  async listServiceAccounts() {
    return this.get('/system-config/service-accounts');
  }

  async createServiceAccount(input: {
    name: string;
    credentialsJson: string;
    enabled?: boolean;
  }) {
    return this.post('/system-config/service-accounts', input);
  }

  async updateServiceAccount(
    saId: string,
    input: { name?: string; enabled?: boolean }
  ) {
    return this.patch(
      `/system-config/service-accounts/${encodeURIComponent(saId)}`,
      input
    );
  }

  async deleteServiceAccount(saId: string) {
    return this.delete(`/system-config/service-accounts/${encodeURIComponent(saId)}`);
  }

  /**
   * Merchant API developer registration (docs/gmc-validation.md).
   * Called with no params it only reads the stored receipt (no Google
   * round-trip); with both params it additionally asks Google for live
   * status. These endpoints answer at the top level rather than under
   * `data`, hence the cast.
   */
  async getMerchantApiRegistration(params?: {
    serviceAccountId: string;
    accountId: string;
  }): Promise<MerchantApiRegistrationResponse> {
    const res = await this.get('/system-config/merchant-api/registration', params);
    return res as unknown as MerchantApiRegistrationResponse;
  }

  async registerMerchantApiDeveloper(input: {
    serviceAccountId: string;
    accountId: string;
    /** Optional — when set, Google grants that account the API_DEVELOPER
     *  role, which requires Admin access on the Merchant Center account. */
    developerEmail?: string;
  }): Promise<MerchantApiRegisterResponse> {
    const res = await this.post('/system-config/merchant-api/registration', input);
    return res as unknown as MerchantApiRegisterResponse;
  }

  // Audit Logs API
  /**
   * `GET /audit-logs` answers `{ success, results, pagination }` (not the usual
   * `result`), newest first, with `user` populated as `{ _id, name, email }`.
   */
  async getAuditLogs(params?: AuditLogQuery): Promise<AuditLogListResult> {
    return this.get('/audit-logs', params) as unknown as Promise<AuditLogListResult>;
  }

  async getAuditLogStats() {
    return this.get('/audit-logs/stats');
  }

  /**
   * Downloads the audit-log export (`GET /audit-logs/export`, same
   * `system:view_audit_logs` gate as the list). Fetched through the
   * authenticated axios instance as a Blob — auth is a Bearer header, so a
   * plain anchor navigation would not be authorised.
   */
  async exportAuditLogs(params: AuditLogQuery & { format?: 'csv' | 'json' } = {}): Promise<Blob> {
    const response = await this.api.get('/audit-logs/export', {
      params: { format: 'csv', ...params },
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  // GDPR API methods
  async requestDataAccess(userId: string, reason?: string) {
    return this.post('/gdpr/data-access-request', { userId, reason });
  }

  async requestDataErasure(userId: string, reason?: string, confirmErasure: boolean = true) {
    return this.post('/gdpr/data-erasure-request', { userId, reason, confirmErasure });
  }

  async recordConsent(consentType: string, granted: boolean, version?: string) {
    return this.post('/gdpr/consent', { consentType, granted, version });
  }

  async getConsentStatus(consentType: string) {
    return this.get(`/gdpr/consent/${consentType}`);
  }

  async getPrivacyPolicy() {
    return this.get('/gdpr/privacy-policy');
  }

  async getDataRetentionInfo() {
    return this.get('/gdpr/data-retention-info');
  }

  // Project Assignment methods
  async getProjectAssignments(params?: any) {
    return this.get('/project-assignments', params);
  }

  async getProjectAssignment(id: string) {
    return this.get(`/project-assignments/${id}`);
  }

  async createProjectAssignment(data: any) {
    return this.post('/project-assignments', data);
  }

  async updateProjectAssignment(id: string, data: any) {
    return this.put(`/project-assignments/${id}`, data);
  }

  async deleteProjectAssignment(id: string) {
    return this.delete(`/project-assignments/${id}`);
  }

  async getProjectAssignmentStats(params?: any) {
    return this.get('/project-assignments/stats', params);
  }

  async checkAssignmentConflicts(data: any) {
    return this.post('/project-assignments/check-conflicts', data);
  }

  async transferProject(projectId: string, data: any) {
    return this.post(`/projects/${projectId}/transfer`, data);
  }

  // Retrospective analysis methods
  async getEstimationAccuracyAnalysis(params?: { timeframe?: string; podId?: string }) {
    return this.get('/retrospective/estimation-accuracy', params);
  }

  async getProjectCostAnalysis(params?: { timeframe?: string; clientId?: string }) {
    return this.get('/retrospective/project-costs', params);
  }

  async getResourceUtilizationAnalysis(params?: { timeframe?: string; podId?: string }) {
    return this.get('/retrospective/resource-utilization', params);
  }

  async getComprehensiveRetrospectiveAnalysis(params?: { timeframe?: string; podId?: string; clientId?: string }) {
    return this.get('/retrospective/comprehensive', params);
  }

  async getRetrospectiveTrends(params?: { metric?: string; podId?: string; clientId?: string }) {
    return this.get('/retrospective/trends', params);
  }

  async getRetrospectiveInsights(params?: { timeframe?: string; podId?: string }) {
    return this.get('/retrospective/insights', params);
  }

  // Import job management (legacy methods - use importJobsService for new code)
  async restartImportJob(jobId: string) {
    return this.post(`/imports/jobs/${jobId}/restart`);
  }

  async cancelImportJob(jobId: string, reason?: string) {
    return this.post(`/imports/jobs/${jobId}/cancel`, { reason });
  }

  // Import jobs - optimized methods
  async getImportJobsList(params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    source?: string;
    userId?: string;
  }) {
    return this.get('/imports/jobs', { params });
  }

  async getImportJobDetails(jobId: string, params?: {
    includeLogs?: boolean;
    logsLimit?: number;
    includeQueue?: boolean;
    includeHealth?: boolean;
  }) {
    return this.get(`/imports/jobs/${jobId}`, { params });
  }

  async getImportStats() {
    return this.get('/imports/stats');
  }

  // Performance monitoring methods
  async getPerformanceStats(timeRange?: number) {
    const params = timeRange ? { timeRange } : {};
    return this.get('/performance/stats', params);
  }

  async getPerformanceMemory(timeRange?: number) {
    const params = timeRange ? { timeRange } : {};
    return this.get('/performance/memory', params);
  }

  async getPerformanceHealth() {
    return this.get('/performance/health');
  }

  async forceGarbageCollection() {
    return this.post('/performance/gc');
  }

  // Admin Tools API
  async reparseJiraWorklogs() {
    return this.post('/admin-tools/reparse-jira-worklogs');
  }

  async findDuplicateTimeEntries() {
    return this.post('/admin-tools/find-duplicate-time-entries');
  }

  async cleanDuplicateTimeEntries(dryRun: boolean = true) {
    return this.post('/admin-tools/clean-duplicate-time-entries', { dryRun });
  }

  async analyzeClickUpBilling() {
    return this.post('/admin-tools/analyze-clickup-billing');
  }

  async createMissingProjects(dryRun: boolean = true) {
    return this.post('/admin-tools/create-missing-projects', { dryRun });
  }

  async findMissingJiraUsers(autoMatch: boolean = false) {
    return this.post('/admin-tools/find-missing-jira-users', { autoMatch });
  }

  // BreatheHR Integration methods
  async getBreatheHREmployees(params?: { page?: number; limit?: number }) {
    return this.get('/breathhr/employees', params);
  }

  async getBreatheHRAbsences(params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    status?: string;
  }) {
    return this.get('/breathhr/absences', params);
  }

  async getBreatheHRStats() {
    return this.get('/breathhr/stats');
  }

  async getBreatheHRSummary() {
    return this.get('/breathhr/summary');
  }

  async getBreatheHRSicknesses(params?: { page?: number; limit?: number }) {
    return this.get('/breathhr/sicknesses', params);
  }

  async getBreatheHRSalaries(params?: { page?: number; limit?: number }) {
    return this.get('/breathhr/salaries', params);
  }

  async getBreatheHRBonuses(params?: { page?: number; limit?: number }) {
    return this.get('/breathhr/bonuses', params);
  }

  async getBreatheHRBenefits(params?: { page?: number; limit?: number }) {
    return this.get('/breathhr/benefits', params);
  }

  async getBreatheHRTrainingCourses(params?: { page?: number; limit?: number }) {
    return this.get('/breathhr/training-courses', params);
  }

  async getBreatheHRTrainingTypes() {
    return this.get('/breathhr/training-types');
  }

  async getBreatheHREmployeeJobs(params?: { page?: number; limit?: number }) {
    return this.get('/breathhr/employee-jobs', params);
  }

  async getBreatheHRHolidayAllowances(params?: { page?: number; limit?: number; year?: number }) {
    return this.get('/breathhr/holiday-allowances', params);
  }

  async getBreatheHRLeaveRequests(params?: { page?: number; limit?: number; status?: string }) {
    return this.get('/breathhr/leave-requests', params);
  }

  async getBreatheHRWorkingPatterns() {
    return this.get('/breathhr/working-patterns');
  }

  async getBreatheHRDepartments() {
    return this.get('/breathhr/departments');
  }

  async getBreatheHRDivisions() {
    return this.get('/breathhr/divisions');
  }

  async getBreatheHRLocations() {
    return this.get('/breathhr/locations');
  }

  async getBreatheHRStatutoryHolidays(params?: { year?: number; countryCode?: string }) {
    return this.get('/breathhr/statutory-holidays', params);
  }

  async getBreatheHRExpenses(params?: { page?: number; limit?: number }) {
    return this.get('/breathhr/expenses', params);
  }

  async getBreatheHRExpenseClaims(params?: { page?: number; limit?: number; status?: string }) {
    return this.get('/breathhr/expense-claims', params);
  }

  async getBreatheHRChangeRequests(params?: { page?: number; limit?: number; status?: string }) {
    return this.get('/breathhr/change-requests', params);
  }

  async getBreatheHRCompanyDocuments(params?: { page?: number; limit?: number; category?: string }) {
    return this.get('/breathhr/company-documents', params);
  }

  async getBreatheHRCompanyProjects(params?: { page?: number; limit?: number; status?: string }) {
    return this.get('/breathhr/company-projects', params);
  }

  async triggerBreatheHRImport(data: {
    startDate: string;
    endDate: string;
    importSections?: string[];
    employeeIds?: string[];
    absenceTypes?: string[];
  }) {
    return this.post('/breathhr/import', data);
  }

  async getBreatheHRImportAreas() {
    return this.get('/breathhr/areas');
  }

  // BreatheHR Internal Sync methods
  // GET /breathhr/sync/sections responds flat (`{success, sections}`), not
  // wrapped under `result` like the ApiResponse<T> convention — same
  // bypass idiom as getPresenceSettingsContext below.
  async getBreatheHRSyncSections(): Promise<{
    success: boolean;
    sections: Array<{ key: string; displayName: string; tier: number }>;
  }> {
    return this.get<never>('/breathhr/sync/sections') as unknown as Promise<{
      success: boolean;
      sections: Array<{ key: string; displayName: string; tier: number }>;
    }>;
  }

  // Internal sync runs as a background job (a full run takes ~70s, past the
  // 30s axios timeout) — this returns immediately with a jobId; poll it via
  // getBreatheHRInternalSyncStatus.
  async triggerBreatheHRInternalSync(data?: { sections?: string[] }): Promise<{
    success: boolean;
    jobId: string;
    message?: string;
  }> {
    const response = await this.post<{ jobId: string }>('/breathhr/sync/all-internal', data || {});
    return {
      success: response.success,
      jobId: response.result?.jobId || '',
      message: response.message
    };
  }

  async getBreatheHRInternalSyncStatus(jobId: string) {
    return this.get<{
      jobId: string;
      status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
      progress: number;
      orchestrationResults?: { orchestrationSummary?: BreatheHRMasterSyncResult } | null;
      error?: { message: string; details?: any } | null;
      logs?: Array<{ timestamp: string; level: string; message: string }>;
    }>(`/imports/jobs/${jobId}`, { includeQueue: false, includeHealth: false, logsLimit: 1 });
  }

  async triggerBreatheHRSectionSync(section: string, data?: any) {
    return this.post<{
      created: number;
      updated: number;
      skipped: number;
      errors: Array<{ breathhrId: string; error: string }>;
    }>(`/breathhr/sync/${section}`, data || {});
  }

  // Holiday Management methods
  async getHolidays(params?: {
    status?: string | string[];
    type?: string | string[];
    startDate?: string;
    endDate?: string;
    year?: number;
    userId?: string;
    podId?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    return this.get('/holidays', params);
  }

  async getMyHolidays(params?: {
    status?: string | string[];
    type?: string | string[];
    year?: number;
    limit?: number;
  }) {
    return this.get('/holidays/my-requests', params);
  }

  async getPendingHolidayApprovals() {
    return this.get('/holidays/pending-approvals');
  }

  async getHolidayCalendar(params: {
    startDate: string;
    endDate: string;
    podId?: string;
    userIds?: string[];
    includePublicHolidays?: boolean;
    countryCode?: string;
  }) {
    return this.get('/holidays/calendar', params);
  }

  async getHoliday(id: string) {
    return this.get(`/holidays/${id}`);
  }

  async createHoliday(data: {
    type: string;
    startDate: string;
    endDate: string;
    isHalfDayStart?: boolean;
    isHalfDayEnd?: boolean;
    halfDayStartPeriod?: 'am' | 'pm';
    halfDayEndPeriod?: 'am' | 'pm';
    reason?: string;
    notes?: string;
    deductFromAllowance?: boolean;
  }) {
    return this.post('/holidays', data);
  }

  async updateHoliday(id: string, data: {
    type?: string;
    startDate?: string;
    endDate?: string;
    isHalfDayStart?: boolean;
    isHalfDayEnd?: boolean;
    halfDayStartPeriod?: 'am' | 'pm';
    halfDayEndPeriod?: 'am' | 'pm';
    reason?: string;
    notes?: string;
  }) {
    return this.put(`/holidays/${id}`, data);
  }

  async cancelHoliday(id: string, reason?: string) {
    return this.post(`/holidays/${id}/cancel`, { reason });
  }

  async submitHolidayForApproval(id: string) {
    return this.post(`/holidays/${id}/submit`);
  }

  async approveHoliday(id: string, notes?: string) {
    return this.post(`/holidays/${id}/approve`, { notes });
  }

  async rejectHoliday(id: string, reason: string) {
    return this.post(`/holidays/${id}/reject`, { reason });
  }

  /** Conflict panel data for the approvals queue (docs/holiday-approval-rules.md, Task 6): requester's pod/department absences in the window around the request. */
  async getHolidayApprovalContext(id: string, params?: { weeksBefore?: number; weeksAfter?: number }) {
    return this.get(`/holidays/${id}/context`, params);
  }

  // Holiday approval rules (docs/holiday-approval-rules.md, Task 5a/Task 7) —
  // all gated by `holidays:rules` (a SEPARATE action from `holidays:approve`),
  // so callers must check the permission first rather than letting the GET 403.
  // `/approval-rules*` is declared BEFORE `/:id` on the router, so
  // `approval-rules` is never parsed as a holiday id.

  /** The default rule, every named rule with assignee counts, and every active user's resolved rule. */
  async getApprovalRulesOverview() {
    return this.get<never>('/holidays/approval-rules') as unknown as Promise<ApprovalRulesOverviewResponse>;
  }

  async putDefaultApprovalRule(body: ApprovalRuleCadenceInput) {
    return this.put<never>('/holidays/approval-rules/default', body) as unknown as Promise<ApprovalRuleResult>;
  }

  /**
   * Retires the default rule outright — no user reads `source: 'default'` any
   * more and requests fall back to legacy single-approver behaviour. Distinct
   * from saving it with `active: false`, which parks the approver set on file.
   */
  async deleteDefaultApprovalRule() {
    return this.delete<never>(
      '/holidays/approval-rules/default'
    ) as unknown as Promise<ApprovalRuleDeleteDefaultResult>;
  }

  async createNamedApprovalRule(body: NamedApprovalRuleInput) {
    return this.post<never>('/holidays/approval-rules/rules', body) as unknown as Promise<NamedApprovalRuleResult>;
  }

  /** PUT replaces the WHOLE approver set — send the full form state, never a delta. */
  async updateNamedApprovalRule(ruleId: string, body: NamedApprovalRuleInput) {
    return this.put<never>(
      `/holidays/approval-rules/rules/${ruleId}`,
      body
    ) as unknown as Promise<NamedApprovalRuleResult>;
  }

  /**
   * Refuses with 409 (`{ error, assignedCount }` on the TOP level, not in
   * `data`) while anybody still follows the rule — deleting is never a cascade.
   */
  async deleteNamedApprovalRule(ruleId: string) {
    return this.delete<never>(
      `/holidays/approval-rules/rules/${ruleId}`
    ) as unknown as Promise<NamedApprovalRuleDeleteResult>;
  }

  /**
   * Bulk-links people to a named rule, REPLACING whatever override each of them
   * had (exempt included). Existing overrides keep their `effectiveFrom`; newly
   * assigned people start today. Unassigning is a separate
   * `deleteUserApprovalRule` — this endpoint only ever adds/replaces, and it
   * 400s outright on an INACTIVE rule (assigning would otherwise be a silent
   * no-op at read time).
   */
  async assignNamedApprovalRule(ruleId: string, userIds: string[]) {
    return this.post<never>(`/holidays/approval-rules/rules/${ruleId}/assign`, {
      userIds,
    }) as unknown as Promise<ApprovalRuleAssignResult>;
  }

  /**
   * Upserts one person's override — exempt | rule-linked | inline approver set,
   * exactly one of the three (mixing is a 400). PUT replaces wholesale, so an
   * omitted `effectiveFrom` CLEARS the stored date: always send it explicitly.
   */
  async putUserApprovalRule(userId: string, body: ApprovalRuleUserInput) {
    return this.put<never>(
      `/holidays/approval-rules/user/${userId}`,
      body
    ) as unknown as Promise<ApprovalRuleResult>;
  }

  /** Removes a per-user override so the person falls back to the default rule. */
  async deleteUserApprovalRule(userId: string) {
    return this.delete<never>(
      `/holidays/approval-rules/user/${userId}`
    ) as unknown as Promise<ApprovalRuleDeleteResult>;
  }

  async validateHolidayDates(data: { startDate: string; endDate: string; excludeHolidayId?: string }) {
    return this.post('/holidays/validate', data);
  }

  async calculateHolidayWorkingDays(data: {
    startDate: string;
    endDate: string;
    isHalfDayStart?: boolean;
    isHalfDayEnd?: boolean;
  }) {
    return this.post('/holidays/calculate-days', data);
  }

  // Holiday Allowance methods
  async getHolidayAllowances(params?: {
    userId?: string;
    year?: number;
    page?: number;
    limit?: number;
  }) {
    return this.get('/holiday-allowances', params);
  }

  async getMyHolidayAllowance(year?: number) {
    return this.get('/holiday-allowances/my-allowance', year ? { year } : undefined);
  }

  async getTeamAllowanceSummary() {
    return this.get('/holiday-allowances/team-summary');
  }

  async getPodAllowanceSummary(podId: string) {
    return this.get(`/holiday-allowances/pod-summary/${podId}`);
  }

  async getHolidayAllowance(id: string) {
    return this.get(`/holiday-allowances/${id}`);
  }

  async createHolidayAllowance(data: {
    userId: string;
    year: number;
    entitlement: number;
    carryOver?: number;
    carryOverExpiry?: string;
  }) {
    return this.post('/holiday-allowances', {
      userId: data.userId,
      year: data.year,
      entitlement: data.entitlement,
      carriedOver: data.carryOver,
      carriedOverExpiry: data.carryOverExpiry
    });
  }

  async updateHolidayAllowance(id: string, data: {
    entitlement?: number;
    carryOver?: number;
    carryOverExpiry?: string;
  }) {
    return this.put(`/holiday-allowances/${id}`, {
      entitlement: data.entitlement,
      carriedOver: data.carryOver,
      carriedOverExpiry: data.carryOverExpiry
    });
  }

  async adjustHolidayAllowance(id: string, data: {
    amount: number;
    reason: string;
  }) {
    return this.post(`/holiday-allowances/${id}/adjust`, data);
  }

  async recalculateHolidayAllowance(id: string) {
    return this.post(`/holiday-allowances/${id}/recalculate`);
  }

  async deleteHolidayAllowance(id: string) {
    return this.delete(`/holiday-allowances/${id}`);
  }

  // Holiday Allowance Schemes (policy templates)
  async getHolidayAllowanceSchemes(params?: { includeInactive?: boolean }) {
    return this.get<{ success: boolean; schemes: HolidayAllowanceSchemeWithCount[] }>(
      '/holiday-allowances/schemes',
      params
    );
  }

  async getHolidayAllowanceScheme(id: string) {
    return this.get<{ success: boolean; scheme: HolidayAllowanceSchemeWithCount }>(
      `/holiday-allowances/schemes/${id}`
    );
  }

  async createHolidayAllowanceScheme(data: CreateSchemeDTO) {
    return this.post<{ success: boolean; scheme: HolidayAllowanceScheme; message: string }>(
      '/holiday-allowances/schemes',
      data
    );
  }

  async updateHolidayAllowanceScheme(id: string, data: UpdateSchemeDTO) {
    return this.put<{ success: boolean; scheme: HolidayAllowanceScheme; message: string }>(
      `/holiday-allowances/schemes/${id}`,
      data
    );
  }

  async deleteHolidayAllowanceScheme(id: string) {
    return this.delete<{ success: boolean; message: string }>(
      `/holiday-allowances/schemes/${id}`
    );
  }

  async createYearlyHolidayAllowances(data: {
    year: number;
    userIds?: string[];
    defaultEntitlement: number;
  }) {
    return this.post('/holiday-allowances/create-yearly', data);
  }

  // =============================================
  // HOLIDAY ALLOWANCE ADMIN ENDPOINTS
  // =============================================

  async getAdminAllowances(params: {
    year: number;
    department?: string;
    hasAllowance?: boolean;
    searchTerm?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    return this.get('/holiday-allowances/admin/list', params);
  }

  async getAllowanceHistory(allowanceId: string) {
    return this.get(`/holiday-allowances/${allowanceId}/history`);
  }

  async getHolidayWorkingPatterns() {
    return this.get('/holiday-allowances/working-patterns');
  }

  async bulkCreateAllowances(data: {
    year: number;
    userIds: string[];
    defaultEntitlement: number;
    carryOverFromPreviousYear?: boolean;
    carryOverLimit?: number;
    carryOverExpiryMonths?: number;
  }) {
    return this.post('/holiday-allowances/bulk-create', data);
  }

  async processCarryOver(data: {
    fromYear: number;
    toYear: number;
    maxCarryOver: number;
    expiryMonths: number;
    userIds?: string[];
  }) {
    return this.post('/holiday-allowances/process-carry-over', data);
  }

  async calculateProRata(data: {
    userId: string;
    year: number;
    joinDate: string;
    fullYearEntitlement: number;
  }) {
    return this.post('/holiday-allowances/calculate-pro-rata', data);
  }

  async bulkAdjustAllowances(data: {
    userIds: string[];
    amount: number;
    reason: string;
    year?: number;
  }) {
    return this.post('/holiday-allowances/bulk-adjust', data);
  }

  /**
   * Sync holiday allowance schemes from BreatheHR
   */
  async syncHolidayAllowanceSchemesFromBreathhr() {
    return this.post('/breathhr/sync/holiday-allowances', {});
  }

  /**
   * Sync leave balances from employee holiday allowance schemes
   */
  async syncLeaveBalancesFromBreathhr(year: number) {
    return this.post('/breathhr/sync/leave-balances', { year });
  }

  /**
   * Legacy alias - syncs both schemes and balances
   */
  async syncAllowancesFromBreathhr(year: number) {
    // First sync schemes, then sync balances
    await this.syncHolidayAllowanceSchemesFromBreathhr();
    return this.syncLeaveBalancesFromBreathhr(year);
  }

  // =============================================
  // GOOGLE CALENDAR INTEGRATION
  // =============================================

  async getGoogleCalendarConnectionStatus() {
    return this.get<{
      connected: boolean;
      email?: string;
      lastSyncedAt?: string;
      errorCount?: number;
    }>('/auth/google-calendar/status');
  }

  async getGoogleCalendarAuthUrl() {
    return this.get<{ authUrl: string }>('/auth/google-calendar/connect');
  }

  async disconnectGoogleCalendar() {
    return this.post('/auth/google-calendar/disconnect');
  }

  async syncHolidaysToGoogleCalendar() {
    return this.post<{ synced: number; failed: number; message: string }>('/auth/google-calendar/sync');
  }

  // =============================================
  // HR Internal Data Methods
  // =============================================

  // Sickness Records
  async getMySicknessRecords(params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.get('/sickness/my-records', params);
  }

  async getAllSicknessRecords(params?: {
    userId?: string;
    status?: string;
    sicknessType?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/sickness', params);
  }

  async getSicknessRecord(id: string) {
    return this.get(`/sickness/${id}`);
  }

  // Training Courses
  async getMyTrainingCourses(params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.get('/training/my-courses', params);
  }

  async getAllTrainingCourses(params?: {
    userId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/training', params);
  }

  async getTrainingCourse(id: string) {
    return this.get(`/training/${id}`);
  }

  // Expenses
  async getMyExpenses(params?: {
    status?: string;
    category?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.get('/expenses/my-expenses', params);
  }

  async getAllExpenses(params?: {
    userId?: string;
    status?: string;
    category?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/expenses', params);
  }

  async getExpense(id: string) {
    return this.get(`/expenses/${id}`);
  }

  // Expense Claims
  async getMyExpenseClaims(params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.get('/expenses/claims/my-claims', params);
  }

  async getAllExpenseClaims(params?: {
    userId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/expenses/claims', params);
  }

  async getExpenseClaim(id: string) {
    return this.get(`/expenses/claims/${id}`);
  }

  // Benefits
  async getMyBenefits(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.get('/benefits/my-benefits', params);
  }

  async getAllBenefits(params?: {
    userId?: string;
    status?: string;
    benefitType?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/benefits', params);
  }

  async getBenefit(id: string) {
    return this.get(`/benefits/${id}`);
  }

  // Admin: Departments
  async getDepartments(params?: {
    isActive?: boolean;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/departments', params);
  }

  async getDepartment(id: string) {
    return this.get(`/departments/${id}`);
  }

  // Admin: Divisions
  async getDivisions(params?: {
    isActive?: boolean;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/divisions', params);
  }

  async getDivision(id: string) {
    return this.get(`/divisions/${id}`);
  }

  // Admin: Locations
  async getLocations(params?: {
    isActive?: boolean;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/locations', params);
  }

  async getLocation(id: string) {
    return this.get(`/locations/${id}`);
  }

  // Admin: Working Patterns
  async getWorkingPatterns(params?: {
    isActive?: boolean;
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    return this.get('/working-patterns', params);
  }

  async getWorkingPattern(id: string) {
    return this.get(`/working-patterns/${id}`);
  }

  // Worktime report (docs/worktime-presence.md, item 8)
  async getWorktimeReport(from: string, to: string) {
    return this.get<never>('/worktime/report', { from, to }) as unknown as Promise<WorktimeReportResult>;
  }

  // Worktime HR team-calendar matrix (docs/worktime-hr-reporting.md, H2/H5).
  // Array filters are comma-joined into a single string param — mirrors
  // getPodMetrics()/parseListParam on the backend, which accepts either a
  // repeated query param or a single comma-separated value.
  async getWorktimeMatrix(params: WorktimeMatrixParams) {
    const query: Record<string, string> = { from: params.from, to: params.to };
    if (params.podIds && params.podIds.length > 0) query.podIds = params.podIds.join(',');
    if (params.departments && params.departments.length > 0) query.departments = params.departments.join(',');
    if (params.divisions && params.divisions.length > 0) query.divisions = params.divisions.join(',');
    if (params.search) query.search = params.search;
    return this.get<never>('/worktime/matrix', query) as unknown as Promise<WorktimeMatrixResult>;
  }

  // Worktime HR single-person report + day corrections (docs/worktime-hr-reporting.md, H3/H4/H6).
  async getWorktimePersonReport(userId: string, from: string, to: string) {
    return this.get<never>(`/worktime/users/${userId}/report`, { from, to }) as unknown as Promise<WorktimePersonReportResult>;
  }

  /**
   * Fetches the XLSX export as a raw Blob — auth is Bearer (not a cookie the
   * browser would send on a plain anchor navigation), so the file has to be
   * fetched through the authenticated axios instance first, same pattern as
   * `ssrMonitoringService.downloadAiErrorReport`.
   */
  async downloadWorktimePersonReportXlsx(userId: string, from: string, to: string): Promise<Blob> {
    const response = await this.api.get(`/worktime/users/${userId}/report/export.xlsx`, {
      params: { from, to },
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async putWorktimeDayCorrection(userId: string, day: string, body: WorktimeDayCorrectionRequest) {
    return this.put<never>(`/worktime/users/${userId}/day/${day}`, body) as unknown as Promise<WorktimeDayCorrectionResult>;
  }

  // Office attendance rules (docs/worktime-office-rules.md, R2/R5/R6) — all
  // gated by `worktime:rules`, so callers must check the permission first
  // rather than letting the GET 403.
  async getOfficeRules() {
    return this.get<never>('/worktime/office-rules') as unknown as Promise<OfficeRulesResult>;
  }

  async putDefaultOfficeRule(body: OfficeRuleInput) {
    return this.put<never>('/worktime/office-rules/default', body) as unknown as Promise<OfficeRuleResult>;
  }

  /**
   * Retires the default rule outright — nobody is checked and no user reads
   * `source: 'default'` any more. Distinct from saving it with `active: false`,
   * which parks the cadence on file for later.
   */
  async deleteDefaultOfficeRule() {
    return this.delete<never>('/worktime/office-rules/default') as unknown as Promise<OfficeRuleDeleteDefaultResult>;
  }

  async putUserOfficeRule(userId: string, body: OfficeRuleUserInput) {
    return this.put<never>(`/worktime/office-rules/${userId}`, body) as unknown as Promise<OfficeRuleResult>;
  }

  /** Removes a per-user override so the user falls back to the default rule. */
  async deleteUserOfficeRule(userId: string) {
    return this.delete<never>(`/worktime/office-rules/${userId}`) as unknown as Promise<OfficeRuleDeleteResult>;
  }

  // Named, reusable rules (R6). `/office-rules/rules*` is a SUBTREE of the
  // office-rules router declared before `/:userId`, so `rules` is never parsed
  // as a user id.
  async createNamedOfficeRule(body: NamedOfficeRuleInput) {
    return this.post<never>('/worktime/office-rules/rules', body) as unknown as Promise<NamedOfficeRuleResult>;
  }

  /** PUT replaces the WHOLE cadence — send the full form state, never a delta. */
  async updateNamedOfficeRule(ruleId: string, body: NamedOfficeRuleInput) {
    return this.put<never>(`/worktime/office-rules/rules/${ruleId}`, body) as unknown as Promise<NamedOfficeRuleResult>;
  }

  /**
   * Refuses with 409 (`{ error, assignedCount }` on the TOP level, not in
   * `data`) while anybody still follows the rule — deleting is never a cascade.
   */
  async deleteNamedOfficeRule(ruleId: string) {
    return this.delete<never>(
      `/worktime/office-rules/rules/${ruleId}`
    ) as unknown as Promise<NamedOfficeRuleDeleteResult>;
  }

  /**
   * Bulk-links people to a named rule, REPLACING whatever override each of them
   * had (exempt included). Existing assignments keep their `effectiveFrom`;
   * newly assigned people start today. Unassigning is a separate
   * `deleteUserOfficeRule` — this endpoint only ever adds/replaces.
   */
  async assignNamedOfficeRule(ruleId: string, userIds: string[]) {
    return this.post<never>(`/worktime/office-rules/rules/${ruleId}/assign`, {
      userIds,
    }) as unknown as Promise<OfficeRuleAssignResult>;
  }

  /*
   * Single-window exceptions ("waivers", R8/R9). `/office-rules/waivers*` is
   * another literal subtree declared before `/:userId`. The matrix response
   * already carries every window's status + waiver details, so the UI grants,
   * re-words and revokes exceptions here and then simply refetches the matrix —
   * there is no separate list fetch to keep in sync.
   */

  /**
   * Grants an exception for ONE window. `windowFrom`/`windowTo` must be the FULL
   * window bounds from `compliance[]`. A window that already has an exception is
   * a 409 carrying the existing `waiverId` on the TOP level (not in `data`), so
   * the caller can switch to editing its reason instead of overwriting the
   * record of why the first decision was made.
   */
  async createOfficeRuleWaiver(body: OfficeRuleWaiverInput) {
    return this.post<never>('/worktime/office-rules/waivers', body) as unknown as Promise<OfficeRuleWaiverResult>;
  }

  /** Re-words an exception. The window is immutable — only the reason changes. */
  async updateOfficeRuleWaiverReason(waiverId: string, reason: string) {
    return this.put<never>(`/worktime/office-rules/waivers/${waiverId}`, {
      reason,
    }) as unknown as Promise<OfficeRuleWaiverResult>;
  }

  /** Revokes an exception — the window goes back to `violated` on the next evaluation. */
  async deleteOfficeRuleWaiver(waiverId: string) {
    return this.delete<never>(
      `/worktime/office-rules/waivers/${waiverId}`
    ) as unknown as Promise<OfficeRuleWaiverDeleteResult>;
  }

  // Presence Board Settings (docs/worktime-presence.md, FAZA 2 / item S2)
  async getPresenceSettings() {
    return this.get<never>('/worktime/presence-settings') as unknown as Promise<PresenceSettingsResult>;
  }

  async putPresenceSettings(settings: PresenceBoardSettings) {
    return this.put<never>('/worktime/presence-settings', settings) as unknown as Promise<PresenceSettingsResult>;
  }

  async getPresenceSettingsContext() {
    return this.get<never>('/worktime/presence-settings/context') as unknown as Promise<PresenceSettingsContextResult>;
  }

  // Self-service timezone (docs/worktime-presence.md, FAZA 3, T2). Empty
  // string clears the field back to the company default.
  async putMyTimezone(timezone: string) {
    return this.put<never>('/auth/timezone', { timezone }) as unknown as Promise<MyTimezoneResult>;
  }
}

export const apiService = new ApiService();
export default apiService;
